import React, { useState, useEffect } from 'react';
import { chatAPI } from '../api/axios';

const ConversationHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Delete individual session
  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      await chatAPI.deleteConversation(sessionId);
      
      fetchSessions();
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete all sessions
  const deleteAllSessions = async () => {
    if (!window.confirm('Are you sure you want to delete ALL conversations? This cannot be undone.')) return;
    
    try {
      await chatAPI.deleteConversation('all');
      
      setSessions([]);
      setSelectedSession(null);
      setMessages([]);
    } catch (err) {
      setError(err.message);
    }
  };

  // Download CSV
  const downloadCSV = async () => {
    try {
      const response = await chatAPI.exportConversations();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch all sessions
  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations();
      const data = response.data;
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for a specific session
  const fetchMessages = async (sessionId) => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations({ sessionId });
      const data = response.data;
      setMessages(data.messages || []);
      setSelectedSession(sessionId);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#004a97',
          padding: '20px 30px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Conversation History</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>View all user chat sessions</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={downloadCSV}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
              title="Download conversations as CSV"
            >
              📥 Download CSV
            </button>
            <button
              onClick={deleteAllSessions}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
              title="Delete all conversations"
            >
              🗑️ Delete All
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '30px' }}>
          {error && (
            <div style={{ 
              backgroundColor: '#fff5f5', 
              color: '#e53e3e', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              Error: {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '30px' }}>
            {/* Sessions Panel */}
            <div style={{ width: '400px', flexShrink: 0 }}>
              <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Chat Sessions</h2>
              
              <div style={{ 
                maxHeight: '600px', 
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {loading && !sessions.length ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#666' }}>Loading sessions...</p>
                  </div>
                ) : sessions.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    color: '#666',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px'
                  }}>
                    No sessions found
                  </div>
                ) : (
                  sessions.map(session => (
                    <div 
                      key={session.session_id}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        backgroundColor: selectedSession === session.session_id ? '#e6f2ff' : '#f9f9f9',
                        border: selectedSession === session.session_id ? '2px solid #004a97' : '1px solid #eee',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                      onClick={() => fetchMessages(session.session_id)}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>
                        Session: {session.session_id.substring(0, 8)}...
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                        Messages: {session.message_count}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Last: {new Date(session.last_message).toLocaleString()}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.session_id);
                        }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Delete this session"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Messages Panel */}
            <div style={{ flex: 1 }}>
              {selectedSession ? (
                <>
                  <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>
                    Session: {selectedSession}
                  </h2>

                  <div style={{ 
                    maxHeight: '600px', 
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px'
                  }}>
                    {messages.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        color: '#666',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                      }}>
                        No messages found in this session
                      </div>
                    ) : (
                      messages.map((message, index) => (
                        <div 
                          key={message.id}
                          style={{
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            border: '1px solid #eee',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            fontSize: '12px',
                            color: '#666'
                          }}>
                            <div><strong>Message #{index + 1}</strong> | Agent: {message.agent}</div>
                            <div>{new Date(message.timestamp).toLocaleString()}</div>
                          </div>
                          
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#e6f2ff',
                            borderRadius: '6px',
                            marginBottom: '10px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#004a97' }}>User:</div>
                            <div>{message.query}</div>
                          </div>
                          
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#f0fff4',
                            borderRadius: '6px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#28a745' }}>Bot:</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{message.response}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div style={{ 
                  padding: '60px', 
                  textAlign: 'center',
                  color: '#666',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ margin: '0 0 10px', color: '#004a97' }}>Select a Session</h3>
                  <p style={{ margin: 0 }}>Choose a chat session from the left panel to view its messages</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationHistory;